import { isAxiosError } from "axios";

/**
 * Type guard for "this rejected request carried HTTP status N". Narrows the
 * unknown error TanStack Query / promises hand back to a real AxiosError before
 * reading `response.status`, so callers don't hand-roll structural casts.
 */
export const isHttpStatusError = (error: unknown, status: number): boolean =>
  isAxiosError(error) && error.response?.status === status;

export const isNotFoundError = (error: unknown): boolean =>
  isHttpStatusError(error, 404);

// Auth0 SPA SDK error codes meaning the cached session can no longer mint an
// access token silently: only a fresh interactive login recovers. E.g. an Auth0
// Action demanding MFA on a refresh-token exchange throws `mfa_required` from
// every `getAccessTokenSilently()` call, and the request never leaves the browser.
// `timeout` is the silent-auth iframe fallback giving up: Auth0 rejected the
// refresh token and then never answered the iframe (it served an error page).
const UNRECOVERABLE_AUTH0_ERROR_CODES = new Set([
  "mfa_required",
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token",
  "invalid_grant",
  "timeout",
]);

/**
 * True when Auth0 refused to issue an access token for the cached session.
 * Retrying won't help; the agent has to log in again. (A 401 from our own API
 * is handled separately, by the redirect to /logout in services/http.ts.)
 */
export const isSessionExpiredError = (error: unknown): boolean => {
  const code = (error as { error?: unknown } | null)?.error;
  return typeof code === "string" && UNRECOVERABLE_AUTH0_ERROR_CODES.has(code);
};
